import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar as MuiAvatar } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import PersonIcon from '@mui/icons-material/Person';
import styled from 'styled-components';

interface ProfileAvatarProps {
	profilePictureUrl?: string | { publicUrl?: string };
	firstName?: string;
	lastName?: string;
	size?: number;
	onDrop?: (acceptedFiles: File[]) => void;
	onClick?: () => void;
	disabled?: boolean;
}

export default function ProfileAvatar({
	profilePictureUrl,
	firstName = '',
	lastName = '',
	size = 48,
	onDrop,
	onClick,
	disabled = false,
}: ProfileAvatarProps) {
	const [imageError, setImageError] = useState(false);
	const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);

	const imageUrl =
		profilePictureUrl &&
		(typeof profilePictureUrl === 'string'
			? profilePictureUrl
			: profilePictureUrl.publicUrl);

	const hasProfilePicture = imageUrl && !imageError;

	const initials = `${firstName?.charAt(0) || ''}${
		lastName?.charAt(0) || ''
	}`.toUpperCase();

	// Update current image URL when profilePictureUrl changes
	useEffect(() => {
		if (imageUrl !== currentImageUrl) {
			setCurrentImageUrl(imageUrl || null);
			setImageError(false);
		}
	}, [imageUrl, currentImageUrl]);

	const handleImageError = () => {
		setImageError(true);
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
		},
		maxFiles: 1,
		maxSize: 5 * 1024 * 1024, // 5MB
		disabled: disabled || !onDrop,
	});

	const uploadText = hasProfilePicture
		? 'CHANGE PROFILE PHOTO'
		: 'UPLOAD PROFILE PHOTO';

	return (
		<AvatarContainer>
			<AvatarUpload
				{...(onDrop ? getRootProps() : { onClick })}
				size={size}
				$isDragActive={isDragActive}
				$disabled={disabled}
			>
				{onDrop && <input {...getInputProps()} />}
				{hasProfilePicture && currentImageUrl ? (
					<MuiAvatar
						src={currentImageUrl}
						sx={{ width: size, height: size }}
						onError={handleImageError}
					>
						{initials}
					</MuiAvatar>
				) : (
					<InitialsAvatar size={size}>
						{initials || (
							<PersonIcon
								sx={{ fontSize: size * 0.5, color: '#666' }}
							/>
						)}
					</InitialsAvatar>
				)}
			</AvatarUpload>
			{!disabled && (
				<Box
					sx={{ cursor: 'pointer' }}
					onClick={onDrop ? getRootProps().onClick : onClick}
				>
					<Typography
						variant="body2"
						color="primary"
						sx={{ textTransform: 'uppercase' }}
					>
						{uploadText}
					</Typography>
				</Box>
			)}
		</AvatarContainer>
	);
}

const AvatarContainer = styled(Box)`
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 2rem;
`;

interface AvatarUploadProps {
	size: number;
	$isDragActive: boolean;
	$disabled: boolean;
}

const AvatarUpload = styled(Box)<AvatarUploadProps>`
	width: ${({ size }) => size}px;
	height: ${({ size }) => size}px;
	border-radius: 50%;
	border: 2px solid
		${({ $isDragActive, $disabled }) =>
			$disabled ? '#555' : $isDragActive ? '#1976d2' : '#555'};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
	background-color: #3a3a3a;
	transition: border-color 0.2s ease;

	&:hover {
		border-color: ${({ $disabled }) => !$disabled && '#1976d2'};
	}
`;

interface InitialsAvatarProps {
	size: number;
}

const InitialsAvatar = styled(Box)<InitialsAvatarProps>`
	width: ${({ size }) => size}px;
	height: ${({ size }) => size}px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: ${({ size }) => size * 0.4}px;
	font-weight: 600;
	color: #fff;
`;

const ProfileImage = styled.img`
	width: 100%;
	height: 100%;
	border-radius: 50%;
	object-fit: cover;
`;
